import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MapEditorGrid } from './MapEditorGrid'
import { WaveEditorPanel } from './WaveEditorPanel'
import { useMapEditorStore, type EditorSection } from './mapEditorStore'
import { EDITOR_MAX_SIZE, EDITOR_MIN_SIZE, mapFingerprint, type EditorTool } from './editorTypes'
import { downloadMapJson, parseImportedMapJson } from './mapIo'
import { mapDraftStore } from './draftStore'
import { saveEditorDraft } from './draftActions'
import {
  confirmDiscardIfDirty,
  formatValidationError,
  isValidMapId,
  normalizeMapId
} from './editorGuards'
import { useGameStore } from '../store/gameStore'
import { listBuiltinMeta } from '../game/model/mapCatalog'

const TOOLS: Array<{ id: EditorTool; label: string }> = [
  { id: 'grass', label: 'GRASS' },
  { id: 'path', label: 'PATH' },
  { id: 'spawn', label: 'SPAWN' },
  { id: 'exit', label: 'EXIT' }
]

const SECTIONS: Array<{ id: EditorSection; label: string }> = [
  { id: 'map', label: 'MAP' },
  { id: 'waves', label: 'WAVES' },
  { id: 'validation', label: 'VALIDATION' }
]

export const MapEditorPage: React.FC = () => {
  const navigate = useNavigate()
  const startPlaytest = useGameStore((s) => s.startPlaytest)
  const map = useMapEditorStore((s) => s.map)
  const tool = useMapEditorStore((s) => s.tool)
  const section = useMapEditorStore((s) => s.section)
  const hover = useMapEditorStore((s) => s.hover)
  const sourceLabel = useMapEditorStore((s) => s.sourceLabel)
  const baselineJson = useMapEditorStore((s) => s.baselineJson)
  const pastLength = useMapEditorStore((s) => s.past.length)
  const futureLength = useMapEditorStore((s) => s.future.length)
  const setTool = useMapEditorStore((s) => s.setTool)
  const setSection = useMapEditorStore((s) => s.setSection)
  const setMeta = useMapEditorStore((s) => s.setMeta)
  const setDimensions = useMapEditorStore((s) => s.setDimensions)
  const newMap = useMapEditorStore((s) => s.newMap)
  const loadBuiltin = useMapEditorStore((s) => s.loadBuiltin)
  const loadMap = useMapEditorStore((s) => s.loadMap)
  const undo = useMapEditorStore((s) => s.undo)
  const redo = useMapEditorStore((s) => s.redo)
  const markSaved = useMapEditorStore((s) => s.markSaved)
  const validationErrors = useMapEditorStore((s) => s.validationErrors)
  const importRef = useRef<HTMLInputElement>(null)
  const [ioMessage, setIoMessage] = useState<string | null>(null)
  const [draftIds, setDraftIds] = useState(() => mapDraftStore.listDrafts().map((d) => d.id))

  const canUndo = pastLength > 0
  const canRedo = futureLength > 0
  const isDirty = mapFingerprint(map) !== baselineJson
  const errors = useMemo(() => validationErrors(), [map, validationErrors])
  const isValid = errors.length === 0
  const statusLabel = isValid ? 'VALID' : 'INVALID'

  const refreshDrafts = () => setDraftIds(mapDraftStore.listDrafts().map((d) => d.id))

  const handleExport = () => {
    if (!useMapEditorStore.getState().isValid()) {
      setIoMessage('Export blocked — fix validation errors first.')
      setSection('validation')
      return
    }
    downloadMapJson(useMapEditorStore.getState().map)
    setIoMessage(`Exported ${useMapEditorStore.getState().map.id}.json`)
  }

  const handleImportFile = async (file: File) => {
    if (!confirmDiscardIfDirty(useMapEditorStore.getState().isDirty(), 'import this file')) {
      return
    }
    try {
      const text = await file.text()
      const result = parseImportedMapJson(text)
      if (!result.ok) {
        setIoMessage(`Import failed: ${result.errors.join('; ')}`)
        return
      }
      loadMap(result.map, `import:${file.name}`)
      setIoMessage(`Imported ${result.map.id}`)
      refreshDrafts()
    } catch (error) {
      setIoMessage(`Import failed: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  const handleSaveDraft = () => {
    try {
      if (!useMapEditorStore.getState().isValid()) {
        setIoMessage('Save draft blocked — map must be VALID.')
        setSection('validation')
        return
      }
      const current = useMapEditorStore.getState()
      const { map: saved, draftId } = saveEditorDraft(current.map, current.sourceLabel)
      loadMap(saved, `draft:${draftId}`)
      markSaved()
      refreshDrafts()
      setIoMessage(`Saved draft ${draftId}`)
    } catch (error) {
      setIoMessage(`Save failed: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  const handleSaveAs = () => {
    const current = useMapEditorStore.getState().map
    const suggested = current.id.endsWith('-copy') ? current.id : `${current.id}-copy`
    const raw = window.prompt('Save As draft id', suggested)
    if (raw === null) return
    const id = normalizeMapId(raw)
    if (!isValidMapId(id)) {
      setIoMessage('Save As failed — id must be lowercase a-z, 0-9, hyphen.')
      return
    }
    if (mapDraftStore.getDraft(id)) {
      if (!window.confirm(`Draft ${id} exists. Overwrite?`)) return
    }
    try {
      if (!useMapEditorStore.getState().isValid()) {
        setIoMessage('Save As blocked — map must be VALID.')
        return
      }
      const toSave = { ...structuredClone(current), id, name: current.name }
      mapDraftStore.saveDraft(toSave)
      loadMap(toSave, `draft:${id}`)
      markSaved()
      refreshDrafts()
      setIoMessage(`Saved as ${id}`)
    } catch (error) {
      setIoMessage(`Save As failed: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  const handleLoadDraft = (id: string) => {
    if (!confirmDiscardIfDirty(useMapEditorStore.getState().isDirty(), `load draft ${id}`)) {
      return
    }
    const draft = mapDraftStore.getDraft(id)
    if (!draft) {
      setIoMessage(`Draft ${id} not found`)
      return
    }
    loadMap(draft.map, `draft:${id}`)
    setIoMessage(`Loaded draft ${id}`)
  }

  const handleDeleteDraft = (id: string) => {
    mapDraftStore.deleteDraft(id)
    refreshDrafts()
    setIoMessage(`Deleted draft ${id}`)
  }

  const handlePlaytest = () => {
    if (!useMapEditorStore.getState().isValid()) {
      setIoMessage('Playtest blocked — map must be VALID.')
      setSection('validation')
      return
    }
    startPlaytest(useMapEditorStore.getState().map)
    navigate('/')
  }

  const handleNewMap = () => {
    if (!confirmDiscardIfDirty(useMapEditorStore.getState().isDirty(), 'create a new map')) {
      return
    }
    newMap({ width: 18, height: 24, id: 'draft', name: 'Draft' })
  }

  const handleLoadBuiltin = (mapId: string) => {
    if (!confirmDiscardIfDirty(useMapEditorStore.getState().isDirty(), `load ${mapId}`)) {
      return
    }
    if (!loadBuiltin(mapId)) {
      setIoMessage(`Built-in map not found: ${mapId}`)
    }
  }

  useEffect(() => {
    const api = useMapEditorStore
    ;(window as unknown as { __shapesTdMapEditor?: typeof api }).__shapesTdMapEditor = api
    return () => {
      delete (window as unknown as { __shapesTdMapEditor?: typeof api }).__shapesTdMapEditor
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      if (typing) return

      const mod = event.ctrlKey || event.metaKey
      if (mod && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        event.preventDefault()
        undo()
        return
      }
      if (mod && (event.key.toLowerCase() === 'y' || (event.key.toLowerCase() === 'z' && event.shiftKey))) {
        event.preventDefault()
        redo()
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setTool('path')
        return
      }
      const key = event.key.toLowerCase()
      if (key === 'g') setTool('grass')
      if (key === 'p') setTool('path')
      if (key === 's') setTool('spawn')
      if (key === 'e') setTool('exit')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo, setTool])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!useMapEditorStore.getState().isDirty()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  return (
    <div className="map-editor" data-testid="map-editor">
      <header className="map-editor__header">
        <div className="map-editor__header-row">
          <Link to="/" className="map-editor__back">
            ← HOME
          </Link>
          <h1 className="map-editor__title">MAP EDITOR</h1>
          <span
            className={`map-editor__badge${isDirty ? ' map-editor__badge--dirty' : ''}`}
            data-testid="editor-dirty"
          >
            {isDirty ? 'Modified' : 'Saved'}
          </span>
        </div>
        <p className="map-editor__dev-note">Internal / development tool · {sourceLabel}</p>
      </header>

      <section className="map-editor__meta" aria-label="Map settings">
        <label className="map-editor__field">
          <span>ID</span>
          <input
            value={map.id}
            onChange={(e) => setMeta({ id: e.target.value })}
            data-testid="map-id"
          />
        </label>
        <label className="map-editor__field">
          <span>Name</span>
          <input
            value={map.name}
            onChange={(e) => setMeta({ name: e.target.value })}
            data-testid="map-name"
          />
        </label>
        <label className="map-editor__field map-editor__field--narrow">
          <span>W</span>
          <input
            type="number"
            min={EDITOR_MIN_SIZE}
            max={EDITOR_MAX_SIZE}
            value={map.width}
            onChange={(e) => {
              const width = Number(e.target.value)
              if (Number.isInteger(width)) setDimensions(width, map.height)
            }}
            data-testid="map-width"
          />
        </label>
        <label className="map-editor__field map-editor__field--narrow">
          <span>H</span>
          <input
            type="number"
            min={EDITOR_MIN_SIZE}
            max={EDITOR_MAX_SIZE}
            value={map.height}
            onChange={(e) => {
              const height = Number(e.target.value)
              if (Number.isInteger(height)) setDimensions(map.width, height)
            }}
            data-testid="map-height"
          />
        </label>
        <label className="map-editor__field map-editor__field--narrow">
          <span>Coins</span>
          <input
            type="number"
            min={0}
            value={map.startingGold}
            onChange={(e) => {
              const startingGold = Number(e.target.value)
              if (Number.isInteger(startingGold)) setMeta({ startingGold })
            }}
            data-testid="map-gold"
          />
        </label>
        <label className="map-editor__field map-editor__field--narrow">
          <span>Lives</span>
          <input
            type="number"
            min={1}
            value={map.startingLives}
            onChange={(e) => {
              const startingLives = Number(e.target.value)
              if (Number.isInteger(startingLives)) setMeta({ startingLives })
            }}
            data-testid="map-lives"
          />
        </label>
      </section>

      <div className="map-editor__sections" role="tablist" aria-label="Editor sections">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={section === s.id}
            className={`map-editor__section${section === s.id ? ' map-editor__section--active' : ''}`}
            data-testid={`section-${s.id}`}
            onClick={(e) => {
              e.preventDefault()
              setSection(s.id)
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="map-editor__actions">
        <button
          type="button"
          className="map-editor__btn"
          data-testid="new-map"
          onClick={(e) => {
            e.preventDefault()
            handleNewMap()
          }}
        >
          NEW MAP
        </button>
        <label className="map-editor__field map-editor__field--builtin">
          <span>Built-in</span>
          <select
            defaultValue=""
            data-testid="load-builtin"
            onChange={(e) => {
              const mapId = e.target.value
              e.target.value = ''
              if (!mapId) return
              handleLoadBuiltin(mapId)
            }}
          >
            <option value="" disabled>
              Load built-in…
            </option>
            {listBuiltinMeta()
              .filter((m) => m.campaign || m.experimental)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.experimental ? `[TEST] ${m.name}` : m.name} ({m.id})
                </option>
              ))}
          </select>
        </label>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="load-valley-v"
          onClick={(e) => {
            e.preventDefault()
            handleLoadBuiltin('valley-v')
          }}
        >
          LOAD VALLEY-V
        </button>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="load-crossroads-c"
          onClick={(e) => {
            e.preventDefault()
            handleLoadBuiltin('crossroads-c')
          }}
        >
          LOAD CROSSROADS
        </button>
        <button
          type="button"
          className="map-editor__btn"
          disabled={!canUndo}
          data-testid="undo"
          onClick={(e) => {
            e.preventDefault()
            undo()
          }}
        >
          UNDO
        </button>
        <button
          type="button"
          className="map-editor__btn"
          disabled={!canRedo}
          data-testid="redo"
          onClick={(e) => {
            e.preventDefault()
            redo()
          }}
        >
          REDO
        </button>
        <button
          type="button"
          className="map-editor__btn map-editor__btn--play"
          disabled={!isValid}
          data-testid="play"
          title={isValid ? 'Playtest with GameSession' : 'Fix validation errors first'}
          onClick={(e) => {
            e.preventDefault()
            handlePlaytest()
          }}
        >
          PLAYTEST
        </button>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="export-json"
          disabled={!isValid}
          onClick={(e) => {
            e.preventDefault()
            handleExport()
          }}
        >
          EXPORT JSON
        </button>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="import-json"
          onClick={(e) => {
            e.preventDefault()
            importRef.current?.click()
          }}
        >
          IMPORT JSON
        </button>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="save-draft"
          onClick={(e) => {
            e.preventDefault()
            handleSaveDraft()
          }}
        >
          SAVE DRAFT
        </button>
        <button
          type="button"
          className="map-editor__btn"
          data-testid="save-as"
          onClick={(e) => {
            e.preventDefault()
            handleSaveAs()
          }}
        >
          SAVE AS
        </button>
        <input
          ref={importRef}
          type="file"
          accept="application/json,.json"
          hidden
          data-testid="import-file"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) void handleImportFile(file)
          }}
        />
      </div>

      {draftIds.length > 0 && (
        <div className="map-editor__drafts" data-testid="draft-list">
          {draftIds.map((id) => (
            <div key={id} className="map-editor__draft-row">
              <button
                type="button"
                className="map-editor__btn"
                data-testid={`load-draft-${id}`}
                onClick={() => handleLoadDraft(id)}
              >
                LOAD {id}
              </button>
              <button
                type="button"
                className="map-editor__btn"
                data-testid={`delete-draft-${id}`}
                onClick={() => handleDeleteDraft(id)}
              >
                DELETE
              </button>
            </div>
          ))}
        </div>
      )}

      {ioMessage && (
        <p className="map-editor__io-msg" data-testid="io-message">
          {ioMessage}
        </p>
      )}

      {section === 'map' && (
        <>
          <div className="map-editor__tools" role="toolbar" aria-label="Terrain tools">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`map-editor__tool${tool === t.id ? ' map-editor__tool--active' : ''}`}
                data-testid={`tool-${t.id}`}
                aria-pressed={tool === t.id}
                onClick={(e) => {
                  e.preventDefault()
                  setTool(t.id)
                }}
              >
                {t.label}
              </button>
            ))}
            <span className="map-editor__hover" data-testid="hover-coord">
              {hover ? `${hover.x},${hover.y}` : '—'}
            </span>
          </div>
          <MapEditorGrid />
        </>
      )}

      {section === 'waves' && <WaveEditorPanel />}

      {(section === 'validation' || section === 'map' || section === 'waves') && (
        <section
          className={`map-editor__status${isValid ? ' map-editor__status--ok' : ' map-editor__status--bad'}`}
          data-testid="validation-status"
          aria-live="polite"
        >
          <strong>{statusLabel}</strong>
          {!isValid && (
            <ul className="map-editor__errors">
            {errors.map((err, i) => (
              <li key={`${err.rule}-${i}`}>{formatValidationError(err)}</li>
            ))}
            </ul>
          )}
          {isValid && (
            <p className="map-editor__ok-meta">
              Path length {map.path.length} · spawn ({map.spawn.x},{map.spawn.y}) → exit (
              {map.exit.x},{map.exit.y}) · {map.waves.length} waves
            </p>
          )}
        </section>
      )}
    </div>
  )
}
